import {
  CalendarBlankIcon,
  CaretLeftIcon,
  ClockIcon,
  InfoIcon,
  MapPinIcon,
} from '@phosphor-icons/react'
import { AxiosResponse } from 'axios'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Controller } from 'react-hook-form'
import useSWR from 'swr'
import { DigitalFirstAidLanguage } from '@/src/@types/admin'
import { get } from '@/src/BlinkAdminApiClient'
import Button from '@/src/components/core/Button'
import { ConfirmCheckbox } from '@/src/components/core/ConfirmCheckbox'
import { DateInput } from '@/src/components/core/DateInput'
import ErrorMessage from '@/src/components/core/ErrorMessage'
import { Input } from '@/src/components/core/Input'
import { Select } from '@/src/components/core/Select'
import LicenseDialog from '@/src/components/elements/course/LicenseDialog'
import Map from '@/src/components/elements/Map'
import Marker from '@/src/components/elements/Marker'
import {
  BookableCourse,
  formatAddress,
  getCourseDays,
  getCourseTitle,
} from '@/src/components/flch/courseLocation/lib'
import useLaravelForm from '@/src/hooks/useLaravelForm'
import useUtmCache from '@/src/hooks/useUTMCache'
import trackEvent from '@/src/lib/tracking'

type CourseTrackingEvent = {
  event: string
  params?: Record<string, unknown>
}

type CourseTracking = {
  open: CourseTrackingEvent | null
  registration: CourseTrackingEvent | null
} | null

type RegistrationForm = {
  first_name: string
  last_name: string
  street: string
  postal_code: string
  city: string
  email: string
  phone: string
  date_of_birth: string
  driver_license: string
  language: string
  accepts_conditions: boolean
  utm_term: string | undefined
  utm_source: string | undefined
  utm_medium: string | undefined
  ref: string | undefined
}

export default function CourseRegistration({
  course,
}: {
  course: BookableCourse
}) {
  const router = useRouter()
  const { utmData } = useUtmCache()

  const [success, setSuccess] = useState(false)
  const [sending, setSending] = useState(false)

  const [showIDNrExplanation, setShowIDNrExplanation] = useState(false)
  const [userSelectedCanton, setUserSelectedCanton] = useState(
    course.provisionalDrivingLicenseDescription
  )

  const isENothelfer = course.type === 'eNothelfer'
  const isWab = course.type === 'WAB'
  const needsLicenseNumber =
    course.type === 'VKU' || course.type.includes('PGS')

  const { data: languages } = useSWR(
    isENothelfer && 'languages',
    async () => await get<DigitalFirstAidLanguage[]>('/website/v2/languages')
  )

  const { handleSubmit, register, control, errorMessage, reset } =
    useLaravelForm<RegistrationForm>({
      defaultValues: {
        accepts_conditions: false,
        utm_term: utmData.utmTerm,
        utm_source: utmData.utmSource,
        utm_medium: utmData.utmMedium,
        ref: utmData.ref,
      },
    })

  const postalCodeRegister = register('postal_code')
  const cityRegister = register('city')

  const registrationUrl = `${process.env.NEXT_PUBLIC_BLINK_ADMIN_URL}/api/fahrlehrer-ch/courses/${course.slug}/register`

  const tracking = course.tracking as unknown as CourseTracking

  useEffect(() => {
    if (tracking && tracking.open) {
      trackEvent(tracking.open)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const closeForm = () => {
    const { course: _course, ...query } = router.query
    router.push({ query }, undefined, { shallow: true })
  }

  return (
    <div>
      {needsLicenseNumber && (
        <LicenseDialog
          show={showIDNrExplanation}
          close={() => setShowIDNrExplanation(false)}
          userSelectedCanton={userSelectedCanton}
          setUserSelectedCanton={setUserSelectedCanton}
        />
      )}
      <button
        type="button"
        onClick={closeForm}
        className="inline-flex items-center gap-1 text-sm font-bold text-blue-700 hover:text-blue-800"
      >
        <CaretLeftIcon size={20} weight="bold" className="shrink-0" />
        Zur Kursbeschreibung
      </button>

      <div className="mt-8 lg:flex lg:gap-16 xl:gap-24">
        <div className="mb-8 lg:order-2 lg:mb-0 lg:w-116 lg:shrink-0">
          <SelectedCourseCard course={course} />
        </div>

        <div className="@container lg:order-1 lg:min-w-0 lg:flex-1">
          {success ? (
            <RegistrationSuccess onReset={() => setSuccess(false)} />
          ) : (
            <>
              <h1 className="text-4xl font-black">Anmeldung</h1>
              <p className="mt-4 text-gray-800">
                Gib deine Kontaktdaten ein und überprüfe deine Kursauswahl
                sorgfältig, bevor du abschliesst.
              </p>
              <form
                onSubmit={handleSubmit(
                  (
                    response: AxiosResponse<{
                      enhanced_conversion_data: string
                    }>
                  ) => {
                    if (tracking && tracking.registration) {
                      trackEvent({
                        ...tracking.registration,
                        user_data: response.data.enhanced_conversion_data,
                      })
                    }
                    setSuccess(true)
                    reset()
                    setSending(false)
                  },
                  (data) => {
                    setSending(true)
                    return data
                  },
                  () => {
                    setSending(false)
                  }
                )}
                method="POST"
                action={registrationUrl}
                className="mt-8"
              >
                <div className="grid gap-x-4 gap-y-5 @2xl:grid-cols-2">
                  <Input
                    label="Vorname"
                    kind="firstName"
                    color="white-blue"
                    labelType="block"
                    {...register('first_name')}
                  />
                  <Input
                    label="Name"
                    kind="lastName"
                    color="white-blue"
                    labelType="block"
                    {...register('last_name')}
                  />
                  <Input
                    label="Strasse + Nr."
                    kind="street"
                    color="white-blue"
                    labelType="block"
                    {...register('street')}
                  />
                  <div>
                    <div className="flex gap-3">
                      <div className="max-w-24 shrink">
                        <Input
                          label="PLZ"
                          kind="zip"
                          color="white-blue"
                          labelType="block"
                          hideErrorMessage
                          {...postalCodeRegister}
                        />
                      </div>
                      <div className="grow">
                        <Input
                          label="Ort"
                          kind="place"
                          color="white-blue"
                          labelType="block"
                          hideErrorMessage
                          {...cityRegister}
                        />
                      </div>
                    </div>
                    {(postalCodeRegister.error || cityRegister.error) && (
                      <div>
                        <ErrorMessage>{postalCodeRegister.error}</ErrorMessage>
                        <ErrorMessage>{cityRegister.error}</ErrorMessage>
                      </div>
                    )}
                  </div>
                  <Input
                    label="E-Mail"
                    kind="email"
                    color="white-blue"
                    labelType="block"
                    {...register('email')}
                  />
                  <Input
                    label="Handynummer Teilnehmer:in"
                    kind="tel"
                    color="white-blue"
                    labelType="block"
                    {...register('phone')}
                  />
                  <DateInput
                    label="Geburtsdatum (TT.MM.JJJJ)"
                    color="white-blue"
                    labelType="block"
                    {...register('date_of_birth')}
                  />
                  {isWab && (
                    <div>
                      <Input
                        color="white-blue"
                        labelType="block"
                        label="Führerausweisnummer"
                        {...register('driver_license')}
                      />
                      <p className="mt-1 px-3 text-xs text-gray-600">
                        Steht unter Punkt 5 auf deinem Führerausweis.
                      </p>
                    </div>
                  )}
                  {needsLicenseNumber && (
                    <div className="relative">
                      <Input
                        color="white-blue"
                        labelType="block"
                        label={
                          userSelectedCanton?.labelName ||
                          'Lernfahrausweisnummer'
                        }
                        {...register('driver_license')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowIDNrExplanation(true)}
                        className="absolute right-3 bottom-4 text-blue-700"
                      >
                        <InfoIcon weight="fill" size={24} />
                      </button>
                    </div>
                  )}
                  {isENothelfer && languages && (
                    <Select
                      label="Bevorzugte Sprache (für eLearning)"
                      color="white-blue"
                      labelType="block"
                      {...register('language')}
                    >
                      {languages.map((language) => (
                        <option key={language.code} value={language.code}>
                          {language.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </div>

                <div className="mt-6">
                  <Controller
                    control={control}
                    name="accepts_conditions"
                    render={({ field: { ref, value, onChange } }) => (
                      <ConfirmCheckbox
                        ref={ref}
                        name="accepts_conditions"
                        color="white-blue"
                        required
                        checked={value}
                        onCheckedChange={onChange}
                      >
                        Ich akzeptiere die{' '}
                        <Link
                          href="https://blinkdrive.ch/allgemeine-geschaeftsbedingungen/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline"
                        >
                          allgemeinen Geschäftsbedingungen
                        </Link>
                        . Die{' '}
                        <Link
                          href="https://blinkdrive.ch/datenschutz/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline"
                        >
                          Datenschutzerklärung
                        </Link>{' '}
                        habe ich gelesen und verstanden.
                      </ConfirmCheckbox>
                    )}
                  />
                </div>

                {errorMessage && (
                  <div className="mt-4">
                    <ErrorMessage>{errorMessage}</ErrorMessage>
                  </div>
                )}

                <p className="mt-8 text-gray-800">
                  Die Bezahlung erfolgt nach der Anmeldung: entweder per
                  Rechnung oder bequem online (z. B. mit Kreditkarte oder
                  Twint). Beide Optionen sind einfach und sicher.
                </p>

                <Button
                  type="submit"
                  version="blue"
                  loading={sending}
                  className="mt-8 w-full @lg:w-auto"
                >
                  Verbindlich anmelden
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function SelectedCourseCard({ course }: { course: BookableCourse }) {
  const days = getCourseDays(course)

  return (
    <div>
      <h2 className="text-2xl font-black">Ausgewählter Kurs</h2>
      <div className="mt-6 overflow-hidden rounded-xl border border-gray-150 bg-gray-25">
        <div className="flex flex-col gap-5 px-6 py-5">
          <div className="flex flex-col gap-4">
            <p className="font-black">{getCourseTitle(course)}</p>
            <div className="flex flex-col gap-3">
              {days.map((day) => (
                <div key={day.isoDay} className="flex flex-col gap-3">
                  <div className="flex items-center gap-2 text-gray-800">
                    <CalendarBlankIcon
                      size={20}
                      weight="fill"
                      className="shrink-0 text-gray-600"
                    />
                    <time dateTime={day.isoDay}>{day.dateText}</time>
                  </div>
                  <div className="flex items-center gap-2 text-gray-800">
                    <ClockIcon
                      size={20}
                      weight="fill"
                      className="shrink-0 text-gray-600"
                    />
                    <span>{day.timeText}</span>
                  </div>
                </div>
              ))}
              {course.type === 'eNothelfer' && (
                <div className="flex gap-2 rounded-lg bg-blue-50 px-3 py-2.5 text-sm text-gray-800">
                  <InfoIcon
                    size={20}
                    weight="fill"
                    className="shrink-0 text-blue-700"
                  />
                  <p>
                    <strong>Achtung:</strong> Du musst das E-Learning vor
                    Kursstart absolviert haben, Dauer ca. 3h.
                  </p>
                </div>
              )}
              <div className="flex items-center gap-2 text-gray-800">
                <MapPinIcon
                  size={20}
                  weight="fill"
                  className="shrink-0 text-gray-600"
                />
                <span>{formatAddress(course.address)}</span>
              </div>
            </div>
          </div>
          <p className="font-black">{course.price / 100} CHF</p>
        </div>
        {course.address.latitude !== null &&
          course.address.longitude !== null && (
            <Map
              className="h-45 w-full"
              markers={[[course.address.longitude, course.address.latitude]]}
              interactive={false}
            >
              <Marker
                position={{
                  lat: course.address.latitude,
                  lng: course.address.longitude,
                }}
                icon="course"
                size="sm"
              />
            </Map>
          )}
      </div>

      <div className="mt-8 flex flex-col gap-2">
        <p className="font-black">Kontakt</p>
        <p className="text-gray-800">
          <a href="mailto:info@fahrlehrer.ch" className="hover:underline">
            info@fahrlehrer.ch
          </a>
        </p>
      </div>
    </div>
  )
}

function RegistrationSuccess({ onReset }: { onReset: () => void }) {
  return (
    <div>
      <h1 className="text-4xl font-black">
        Es hat geklappt! Vielen Dank für deine Anmeldung
      </h1>
      <p className="mt-4 text-gray-800">
        Gleich gibt es von uns noch eine Bestätigung per E-Mail und SMS. Falls
        nichts ankommt, schau bitte kurz im Spamordner nach. Und wenn da auch
        nichts ist, dann melde dich bitte bei uns.
      </p>
      <Button version="gray-outlined" onClick={onReset} className="mt-8">
        Weitere Person anmelden
      </Button>
    </div>
  )
}
